if (!customElements.get('aplovio-header')) {
  customElements.define('aplovio-header', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = {signal:this.abort.signal};
      this.section = this.closest('.shopify-section') || this;
      this.section.classList.add('ap-header-section');
      this.section.dataset.sticky = this.dataset.sticky;
      this.surface = this.querySelector('.ap-header__surface');
      this.menu = this.querySelector('aplovio-layer');
      this.trigger = this.querySelector('[data-layer-open]');
      this.lastY = scrollY; this.directionY = scrollY; this.atTop = scrollY < 8;
      this.schedule = () => {if (!this.frame) this.frame = requestAnimationFrame(() => {this.frame = null; this.update();});};
      this.resize = new ResizeObserver(entries => {
        const box = entries[0]?.borderBoxSize;
        const height = (Array.isArray(box) ? box[0] : box)?.blockSize ?? this.surface.getBoundingClientRect().height;
        const style = document.documentElement.style;
        if (style.getPropertyValue('--ap-header-height') !== `${height}px`) style.setProperty('--ap-header-height', `${height}px`);
        this.schedule();
      });
      this.resize.observe(this.surface);
      window.addEventListener('scroll', this.schedule, {...options, passive:true});
      window.addEventListener('resize', this.schedule, options);
      this.addEventListener('pointerenter', () => {this.hover = matchMedia('(hover:hover)').matches; this.schedule();}, options);
      this.addEventListener('pointerleave', () => {this.hover = false; this.schedule();}, options);
      this.addEventListener('focusin', this.schedule, options);
      this.addEventListener('focusout', this.schedule, options);
      document.addEventListener('aplovio:layer-open', this.schedule, options);
      document.addEventListener('aplovio:layer-close', this.schedule, options);
      document.addEventListener('load', this.schedule, {...options,capture:true});
      // The first photo under a transparent header (a hero, or a full-screen product gallery on phones) waits on a tone
      // (aplovio-header.css) only until it has loaded.
      const toned = '[data-ap-header-background] img[fetchpriority=high],main .ag[data-mobile=fullscreen] .ag-image[fetchpriority=high]';
      const markLoaded = image => {if (image.complete && image.naturalWidth > 0) image.setAttribute('data-ap-loaded','');};
      document.querySelectorAll(toned).forEach(markLoaded);
      document.addEventListener('load', event => {if (event.target.matches?.(toned)) markLoaded(event.target);}, {...options,capture:true});
      document.addEventListener('shopify:section:load', this.schedule, options);
      document.addEventListener('aplovio:collection-load', this.schedule, options);
      document.addEventListener('aplovio:care-load', this.schedule, options);
      document.addEventListener('aplovio:blog-load', this.schedule, options);
      document.addEventListener('aplovio:product-ready', this.schedule, options);
      this.menu?.addEventListener('aplovio:layer-open', event => {
        if (event.target !== this.menu.dialog) return;
        this.menuTrigger = event.detail.trigger;
        this.trigger.setAttribute('aria-expanded', 'true');
      }, options);
      this.menu?.addEventListener('aplovio:layer-close', event => {
        if (event.target !== this.menu.dialog) return;
        this.trigger.setAttribute('aria-expanded','false');
        const pending = this.pendingSearch; this.pendingSearch = null;
        if (pending) queueMicrotask(() => {const controller=this.querySelector('aplovio-search'),search=controller?.closest('aplovio-layer');if(search?.isConnected){controller.skipAutofocusOnce=pending.fromMenu;search.open(pending.trigger);}});
      }, options);
      this.addEventListener('click', event => {
        const searchTrigger = event.target.closest('[data-search-open]');
        const search = this.querySelector('aplovio-search')?.closest('aplovio-layer');
        if (!searchTrigger || !search) return;
        event.preventDefault();
        if (this.menu?.dialog?.open) {
          this.pendingSearch = {trigger:this.menuTrigger?.isConnected ? this.menuTrigger : this.querySelector('.ap-header__surface [data-search-open]') || this.trigger,fromMenu:searchTrigger.hasAttribute('data-search-from-menu')};
          this.menu.close('search');
        } else search.open(searchTrigger);
      }, options);
      this.addEventListener('submit', event => {
        if (!event.target.matches('.ap-localization')) return;
        let input = event.target.querySelector('[name=return_to]');
        if (!input) {input = document.createElement('input'); input.type = 'hidden'; input.name = 'return_to'; event.target.append(input);}
        input.value = location.pathname + location.search;
      }, options);
      this.update();
    }
    update() {
      const locked = !!window.AplovioLayers?.top();
      // Body scroll locking resets scrollY; preserve the originating page state.
      const y = locked ? this.lastY : scrollY;
      if (y < 4) this.atTop = true;
      else if (y > 16) this.atTop = false;
      const interactive = locked || this.hover || this.contains(document.activeElement);
      const mobile = matchMedia('(max-width:989px)').matches;
      const nativeProduct = mobile && this.hasAttribute('data-mobile-native') && document.body.dataset.apPage === 'product';
      const sticky = nativeProduct ? 'always' : this.dataset.sticky;
      if (this.section.dataset.sticky !== sticky) this.section.dataset.sticky = sticky;
      const breakpointChanged = this.mobile !== undefined && this.mobile !== mobile;
      this.mobile = mobile;
      const collectionHero = document.querySelector('[data-home-hero], [data-collection-hero]:not([hidden]), [data-care-hero]:not([hidden]), [data-blog-hero]:not([hidden]), [data-article-hero]:not([hidden]), [data-page-hero]:not([hidden])');
      const productGallery = mobile && document.body.dataset.apPage === 'product' ? document.querySelector('main aplovio-gallery') : null;
      const fullscreenGallery = productGallery?.dataset.mobile === 'fullscreen' ? productGallery : null;
      // Header sections cannot inspect another section's rendered Liquid content.
      // Keep the setting separate and resolve the actual gallery in the page DOM.
      const fullscreenEnabled = fullscreenGallery && this.hasAttribute('data-product-fullscreen-overlay');
      const enabled = fullscreenEnabled || (collectionHero && !collectionHero.hasAttribute('data-home-hero') ? collectionHero.dataset[mobile ? 'headerMobile' : 'headerDesktop'] === 'true' : this.dataset[mobile ? 'transparentMobile' : 'transparentDesktop'] === 'true');
      this.baseContrast ||= this.dataset.contrast;
      const contrast = collectionHero && ['dark','light'].includes(collectionHero.dataset.headerContrast) ? collectionHero.dataset.headerContrast : this.baseContrast;
      const image = document.querySelector('[data-ap-header-background] img') || (mobile && document.body.dataset.apPage === 'product' ? document.querySelector('main .ag-item:first-child .ag-image') : null);
      // Commit the measured height before replacing Liquid's initial overlay.
      // ResizeObserver runs later; an undefined height would briefly drop the overlap.
      const header = this.surface.getBoundingClientRect();
      const rootStyle = document.documentElement.style;
      if (rootStyle.getPropertyValue('--ap-header-height') !== `${header.height}px`) rootStyle.setProperty('--ap-header-height', `${header.height}px`);
      const initialOverlay = !this.section.hasAttribute('data-header-initialized') && document.body.hasAttribute(mobile ? 'data-ap-initial-header-mobile' : 'data-ap-initial-header-desktop');
      const overlay = this.section.hasAttribute('data-header-overlay') || initialOverlay;
      const expectedTop = overlay ? header.top : header.bottom;
      const hasLogo = this.querySelector('.ap-header__logo-normal');
      const logoReady = !hasLogo || this.hasAttribute('data-monochrome-logo') || !!this.querySelector(contrast === 'light' ? '.ap-header__logo-light' : '.ap-header__logo-dark');
      // Auto is reserved for a verified crop analyzer. Unknown imagery stays solid.
      const canOverlay = productGallery?.dataset.mobile !== 'framed' && (!nativeProduct || fullscreenGallery) && enabled && logoReady && ['light','dark'].includes(contrast);
      let fits = false;
      // A solid Header does not depend on image geometry. For an eligible
      // overlay, finish every read before committing presentation attributes.
      if (canOverlay && image && (!image.complete || image.naturalWidth > 0)) {
        const bounds = (fullscreenGallery || image).getBoundingClientRect();
        const width = document.documentElement.clientWidth;
        const coversWidth = bounds.left <= 1 && bounds.right >= width - 1;
        const coversHeader = fullscreenGallery
          ? bounds.top <= expectedTop + 3 && bounds.bottom > header.bottom
          : Math.abs(bounds.top - expectedTop) < 3 && bounds.height > header.height;
        fits = coversWidth && coversHeader && getComputedStyle(image).objectFit === 'cover';
      }
      const eligible = canOverlay && fits;
      if (this.dataset.contrast !== contrast) this.dataset.contrast = contrast;
      // Keep the reserved/overlaid layout stable while scrolling and interacting.
      if ((this.atTop || breakpointChanged) && !locked) this.section.toggleAttribute('data-header-overlay', eligible);
      const transparent = eligible && (fullscreenGallery || this.atTop) && !interactive;
      this.toggleAttribute('data-transparent', transparent);
      let hidden = this.section.hasAttribute('data-header-hidden');
      if (Math.abs(y - this.directionY) > 12) {hidden = y > this.directionY; this.directionY = y;}
      this.section.toggleAttribute('data-header-hidden', !nativeProduct && this.dataset.sticky === 'up' && hidden && !this.atTop && !interactive);
      this.lastY = y;
      const reserved = this.section.hasAttribute('data-header-overlay') ? '0px' : 'var(--ap-header-height)';
      if (rootStyle.getPropertyValue('--ap-header-reserved-height') !== reserved) rootStyle.setProperty('--ap-header-reserved-height', reserved);
      if (!this.section.hasAttribute('data-header-initialized')) this.section.setAttribute('data-header-initialized','');
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null; this.pendingSearch = null; this.resize?.disconnect(); cancelAnimationFrame(this.frame); this.frame = null;
      this.section?.classList.remove('ap-header-section');
      this.section?.removeAttribute('data-header-overlay');
      this.section?.removeAttribute('data-header-hidden');
      this.section?.removeAttribute('data-header-initialized');
    }
  });
}
